import { Collection, getFirebase } from 'firestorter';
import chunk from 'lodash.chunk';
import format from 'string-template';

import { firestorePaths, pathPlaceholder } from '../utils/paths';
import { getRequiredEquipment } from '../utils/workout';
import {
  LongSessionStage,
  WorkoutPublishStatus,
  WorkoutSessionType,
} from '../utils/longSession';
import WorkoutDefinition from './WorkoutDefinition';
import BaseDocument from './BaseDocument';

// A document id ("in") query takes at most this many ids on this SDK version (@firebase/firestore 2).
const WORKOUT_IDS_PER_QUERY = 10;

/**
 * Visibility type of workouts
 * - **PUBLIC:** Workout that was visible to all coaches
 * - **PRIVATE:** The Workout, its' visible only to the coach, who owns the workout.
 */
const VisibilityType = {
  PRIVATE: 'PRIVATE',
  PUBLIC: 'PUBLIC',
};

/**
 * Class representing a workout.
 *
 * @class Workout
 * @extends BaseDocument
 */
class Workout extends BaseDocument {
  async init(initOpts) {
    await super.init(initOpts);
    this.workoutDefinition = new WorkoutDefinition(this.data);
  }

  /**
   * Gets the name.
   * @returns {string}
   */
  get name() {
    return this.data.name;
  }

  /**
   * Gets the estimated duration in seconds.
   * @returns {number}
   */
  get estimatedDuration() {
    return this.data.estimatedDuration;
  }

  /**
   * Gets the estimated duration in minutes.
   * @returns {number|void} Estimated duration in minutes or null if not available.
   */
  get estimatedDurationInMinutes() {
    return this.estimatedDuration
      ? Math.ceil(this.estimatedDuration / 60)
      : null;
  }

  /**
   * Gets the activities in the workout.
   * @returns {Array}
   */
  get activities() {
    return this.data.activities;
  }

  /**
   * Checks if the workout is archived.
   * @returns {boolean}
   */
  get isArchived() {
    return !!this.data.isArchived;
  }

  /**
   * Gets the creation timestamp. The value is stored as milliseconds since the Unix epoch.
   * @returns {number}
   */
  get createdAt() {
    return this.data.createdAt;
  }

  /**
   * Gets the visibility type. It will be one of {@link VisibilityType}.
   * @returns {string}
   */
  get visibility() {
    return this.data.visibility;
  }

  /**
   * Gets how the workout is authored and played. One of {@link WorkoutSessionType}; absent means
   * STANDARD, which every pre-existing workout is.
   *
   * Every consumer that asks "is this a long session" reads this field and nothing else, so a
   * workout that fails to expose it is indistinguishable from a standard one — which is how a
   * finished session ends up rejected for holding a session video it is supposed to hold.
   * @returns {string|undefined}
   */
  get sessionType() {
    return this.data.sessionType;
  }

  /**
   * Gets the publish status. One of {@link WorkoutPublishStatus}; absent means READY, so every
   * pre-existing workout stays assignable with no backfill.
   * @returns {string|undefined}
   */
  get publishStatus() {
    return this.data.publishStatus;
  }

  /**
   * Gets the long-session video processing tracker. Present only while a long session is being
   * prepared — it is cleared when the workout reaches READY.
   * @returns {Object|undefined}
   */
  get videoProcessing() {
    return this.data.videoProcessing;
  }

  /**
   * Gets the Storage path of a long session's uploaded original. Written by the builder before the
   * upload starts, so replacing the video always knows exactly which object it supersedes.
   * @returns {string|undefined}
   */
  get sourceStoragePath() {
    return this.data.sourceStoragePath;
  }

  /**
   * Gets the required equipment as an object with the equipment id as key and the equipment tag as the value.
   * @returns {Object}
   */
  get requiredEquipment() {
    return getRequiredEquipment(this.activities);
  }

  /**
   * Gets the equipment names/tags as an array.
   * @returns {Array}
   */
  get equipmentList() {
    return Object.values(this.requiredEquipment);
  }

  /**
   * Adds a new workout.
   *
   * @param {Object} data - The data for the new workout.
   * @returns {Promise<string>} The id of the newly added workout.
   */
  static async addWorkout(data) {
    const newWorkout = new Collection(firestorePaths.WORKOUT);
    const { id } = await newWorkout.add(data);
    return id;
  }

  /**
   * Creates the workout a long-format session is uploaded against.
   *
   * Created *before* the video, and deliberately so: the document is the only durable place the
   * upload status can live. It is what lets a coach start an upload, close the tab, come back and
   * see exactly which stage it reached — and it gives the Storage path a workout id to be keyed on.
   * It is born PENDING, which makes it unassignable until the server marks it READY.
   *
   * @param {Object} params
   * @param {string} params.coachId The coach who will own the workout.
   * @param {string} params.name
   * @param {string} [params.note]
   * @returns {Promise<string>} The id of the new workout.
   */
  static async addPendingLongSessionWorkout({ coachId, name, note }) {
    return Workout.addWorkout({
      name,
      note: note || '',
      coach: coachId,
      createdAt: Date.now(),
      // The single LONG_SESSION activity is written by the server once the video is READY.
      activities: [],
      // Duration comes from the video, so there is nothing to estimate yet.
      estimatedDuration: 0,
      visibility: VisibilityType.PRIVATE,
      isArchived: false,
      sessionType: WorkoutSessionType.LONG_SESSION,
      publishStatus: WorkoutPublishStatus.PENDING,
      videoProcessing: {
        stage: LongSessionStage.UPLOADING,
        updatedAt: getFirebase().firestore.FieldValue.serverTimestamp(),
      },
    });
  }

  /**
   * Points a long session at a new source video and puts it back into its uploading state.
   *
   * The status moves with the path in one write: `longSession-onSourceUploaded` deletes a finalized
   * object unless the workout is PENDING at stage UPLOADING.
   *
   * @param {string} sourceStoragePath Where the new video is about to be uploaded.
   * @returns {Promise<void>}
   */
  async startLongSessionUpload(sourceStoragePath) {
    await this.updateFields({
      sourceStoragePath,
      publishStatus: WorkoutPublishStatus.PENDING,
      videoProcessing: {
        stage: LongSessionStage.UPLOADING,
        updatedAt: getFirebase().firestore.FieldValue.serverTimestamp(),
      },
    });
  }

  /**
   * Records a session whose video never arrived — the coach cancelled, or the SDK gave up.
   *
   * FAILED rather than a status of its own: the panel, the workouts table and the Storage trigger
   * all already treat it as "no video yet, and a new one is welcome", and `cause` carries the
   * difference for anything that wants to word it precisely.
   *
   * @param {string} workoutId
   * @param {Object} params
   * @param {string} params.cause One of `LongSessionFailureCause` — CANCELLED or UPLOAD_ERROR.
   * @param {string} [params.message] The SDK's own message, when there was one.
   * @param {string} [params.cancelledBy] Who stopped it — the logged-in user, who may be a CAM
   * acting for the owning coach rather than the coach themselves.
   * @returns {Promise<void>}
   */
  static async markLongSessionUploadStopped(workoutId, { cause, message = '', cancelledBy }) {
    const workout = await Workout.getWorkoutById(workoutId);
    const error = {
      stage: LongSessionStage.UPLOADING,
      cause,
      message,
    };
    if (cancelledBy) {
      error.cancelledBy = cancelledBy;
    }
    await workout.updateFields({
      publishStatus: WorkoutPublishStatus.FAILED,
      videoProcessing: {
        stage: LongSessionStage.UPLOADING,
        error,
        updatedAt: getFirebase().firestore.FieldValue.serverTimestamp(),
      },
    });
  }

  /**
   * Discards this session: archived, and stamped with who did it. The uploaded object is left in
   * place — archiving is what makes a finalize landing after this get rejected and cleaned up
   * server-side instead of reviving the session.
   *
   * @param {string} removedBy Logged-in user, who may be a CAM acting for the owning coach.
   * @returns {Promise<void>}
   */
  async discardLongSession(removedBy) {
    return this.updateFields({
      isArchived: true,
      removedBy,
      removedAt: new Date(),
    });
  }

  /**
   * Get workouts by coach.
   *
   * @param {string} coachId - The id of the coach.
   * @param {boolean} [fetchArchivedWorkouts=false] - Whether to fetch archived workouts or not.
   * @returns {Promise<Collection>} The collection of workouts of the coach.
   */
  static async getWorkoutsByCoach(coachId, fetchArchivedWorkouts = false) {
    const workoutCollection = new Collection(firestorePaths.WORKOUT,
      {
        query: (ref) => {
          let query = ref
            .where('coach', '==', coachId);
          if (!fetchArchivedWorkouts) {
            query = query.where('isArchived', '==', false);
          }
          return query;
        },
        createDocument: (source, options) => new Workout(source, { ...options, disableObserverRefCount: true }),
      });
    await Workout.initCollection(workoutCollection);
    return workoutCollection;
  }

  /**
   * Get workouts by their document ids (archived or not). Ids with no workout are left out.
   * A document id ("in") query takes at most WORKOUT_IDS_PER_QUERY ids on this SDK version, so the ids
   * are read in chunks.
   *
   * @param {string[]} workoutIds - The ids of the workouts to fetch.
   * @returns {Promise<Workout[]>} The matching workouts.
   */
  static async getWorkoutsByIds(workoutIds) {
    const workoutCollections = await Promise.all(chunk(workoutIds, WORKOUT_IDS_PER_QUERY).map(async (ids) => {
      const workoutCollection = new Collection(firestorePaths.WORKOUT,
        {
          query: (ref) => ref
            .where(getFirebase().firestore.FieldPath.documentId(), 'in', ids),
          createDocument: (source, options) => new Workout(source, { ...options, disableObserverRefCount: true }),
        });
      await Workout.initCollection(workoutCollection);
      return workoutCollection;
    }));
    return workoutCollections.flatMap(({ docs }) => docs);
  }

  /**
   * Get public workouts.
   * - For Coach Assistant Users (ACs): Returns all public workouts.
   * - For Coaches: Returns only public workouts where isClientFacing is true.
   *
   * @param {boolean} isCoach - Whether the user is a coach or not.
   * @returns {Promise<Collection>} The collection of public workouts.
   */
  static async getPublicWorkouts(isCoach) {
    const workoutCollection = new Collection(firestorePaths.WORKOUT,
      {
        query: (ref) => {
          let query = ref.where('visibility', '==', VisibilityType.PUBLIC);

          if (isCoach) {
            query = query.where('isClientFacing', '==', true);
          }
          return query;
        },
        createDocument: (source, options) => new Workout(source, { ...options, disableObserverRefCount: true }),
      });
    await Workout.initCollection(workoutCollection);
    return workoutCollection;
  }

  /**
   * Gets a workout by id.
   *
   * @param {string} workoutID - The id of the workout.
   * @returns {Promise<Workout>} The workout document.
   */
  static async getWorkoutById(workoutID) {
    const workoutDoc = new Workout(format(firestorePaths.WORKOUT_DOC, {
      [pathPlaceholder.WORKOUT_ID]: workoutID,
    }));

    await workoutDoc.init();

    return workoutDoc;
  }
}

export default Workout;
export {
  VisibilityType,
};
